import { useAuth } from "./useAuth";

/**
 * usePermission — boolean check for a single permission key.
 *
 * Mirrors the backend matcher in {@code PermissionService}:
 *   - exact match (e.g. {@code "leaves.approve"})
 *   - god-mode wildcard {@code "*"}
 *   - feature wildcard {@code "leaves.*"}
 *
 * The ADMIN role bypasses every check, so even an empty permission cache
 * still grants every action.
 *
 * Note: this is a *display* gate. The server is authoritative — even if
 * a cached permission incorrectly returns true here, the backend
 * {@code @PreAuthorize} will still 403 the request.
 *
 * Examples:
 *   const canApprove = usePermission("leaves.approve");
 *   if (canApprove) <ApproveButton .../>
 */
export function usePermission(key) {
  const { hasPermission } = useAuth();
  return hasPermission(key);
}

/** True if the user has *any* of the listed permissions. */
export function useAnyPermission(...keys) {
  const { hasPermission } = useAuth();
  return keys.some((k) => hasPermission(k));
}

/** True if the user has *all* of the listed permissions. */
export function useAllPermissions(...keys) {
  const { hasPermission } = useAuth();
  return keys.every((k) => hasPermission(k));
}
